import { createApi } from "../../../utils/createApi";
import { AxiosError, AxiosResponse } from "axios";
import { NavigateFunction } from "react-router-dom";
import { markSiteUnsynced } from "../../../utils/syncStore";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import { createSubmittedCameraObject } from "../../AddCamera/createSubmittedCameraObject";
import { readAddCameraInfoFromStorage } from "../../AddCamera/buildAddCameraWizardResponse";
import { fetchAddCameraInfo } from "../../Sites/SitesPage/SitesPageUtils/fetchAddCameraInfo";
import { DiscoveredCamera, OnvifCredential } from "./onvifScanInterfaces";
import { buildOnvifCameraValues } from "./buildOnvifCameraValues";

/**
 * Auto-add the selected discovered cameras from their RTSP URLs, then refetch
 * the site's cameras. By default navigates to Site About; callers may pass
 * `onSuccess` (e.g. onboarding) to continue a different flow instead.
 *
 * Reuses the manual/NVR add pattern: build each onboarding body with
 * createSubmittedCameraObject (site's "rtsp" integration submit_object + forms),
 * POST camera/onboarding/, then re-fetch camera/site/ and markSiteUnsynced.
 */
export const handleSubmitOnvifCameras = async (
  selectedCameras: DiscoveredCamera[],
  credentials: OnvifCredential[],
  siteId: string,
  setSubmitting: (value: boolean) => void,
  navigate: NavigateFunction,
  tunnelServerId?: number | null,
  onSuccess?: () => void
): Promise<void> => {
  if (selectedCameras.length === 0) {
    return;
  }

  setSubmitting(true);

  // Set the site's WireGuard availability zone to the server behind the tunnel we
  // scanned, so the site is tied to the same AZ as its cameras' tunnel.
  if (tunnelServerId != null) {
    try {
      const customerApi = createApi("");
      // The /about/ action uses SiteAboutSerializer, where `wireguard` is
      // writable (a plain customer PATCH ignores it).
      await customerApi.patch(`customer/${siteId}/about/`, {
        wireguard: tunnelServerId,
      });
    } catch {
      // Non-fatal: cameras still add; AZ can be set later on Site About.
    }
  }

  // Ensure the site's onboarding template (submit_object + forms) is loaded.
  await fetchAddCameraInfo(siteId, navigate, { silent: true });
  const addCameraInfo = readAddCameraInfoFromStorage();
  const submitObject = addCameraInfo?.submit_object;
  const forms = addCameraInfo?.forms;

  const onboardingApi = createApi("camera/onboarding/");
  const failures: string[] = [];

  for (const camera of selectedCameras) {
    const values = buildOnvifCameraValues(camera, credentials);
    const body = createSubmittedCameraObject(values, submitObject, forms);
    try {
      // eslint-disable-next-line no-await-in-loop
      await onboardingApi.post("", body);
    } catch (error) {
      failures.push(`${camera.ip} (${camera.rtspUri})`);
    }
  }

  // Refetch the site cameras so SiteAbout shows the newly added ones on arrival.
  const AxiosUI = createApi("");
  await AxiosUI.get(`camera/site/?customer__id=${siteId}`).then(
    (response: AxiosResponse) => {
      localStorage.setItem("siteCameras", JSON.stringify(response.data));
    },
    (reason: AxiosError) => {
      universalAPIErrorHandler(reason, navigate, () => {}, () => {});
    }
  );

  markSiteUnsynced();
  setSubmitting(false);

  if (failures.length > 0) {
    alert(
      `Some cameras could not be added:\n${failures.join(
        "\n"
      )}\n\nThe rest were added successfully.`
    );
  }

  if (onSuccess) {
    onSuccess();
  } else {
    navigate(`/sites/aboutsite/${siteId}`);
  }
};
